import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useUserData } from "components/customHooks/userDataContext";
import { recommendCPTCodes } from "apiHandler/actions";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const getScoreColor = (score) => {
  if (score >= 85) return "green";
  if (score >= 60) return "goldenrod";
  return "orangered";
};

const CPTOptimizerTool = () => {
  const [note, setNote] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const userData = useUserData();
  const [errorMessage, setErrorMessage] = useState("");

  const practiceId = userData?.selected_practice;
  const token = userData?.access_token;

  const fetchCPTData = async () => {
    setLoading(true);
    setRows([]);
    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = note;
      const plainText = tempDiv.innerText;

      const payload = {
        practice_id: practiceId,
        user_prompt: plainText,
      };

      const res = await recommendCPTCodes(payload, token);
      if (res?.error) {
        // Customize error message for user
        if (res.error.includes("AI Agent")) {
          setErrorMessage(
            "An error occurred while processing the note. Please try again later."
          );
        } else {
          setErrorMessage(res.error);
        }
        return;
      }

      if (Array.isArray(res)) {
        setRows(
          res.map((item, index) => ({
            id: index,
            ...item,
          }))
        );
      }
    } catch (error) {
      console.log("error fetchCPTData: ", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "cpt_code",
      headerName: "CPT Code",
      flex: 1,
    },
    {
      field: "confidence_score",
      headerName: "Confidence Score",
      flex: 1.2,
      renderCell: (params) => (
        <span style={{ color: getScoreColor(params.value) }}>
          {params.value}%
        </span>
      ),
    },
    {
      field: "evidence_found",
      headerName: "Evidence Found",
      flex: 1,
    },
    {
      field: "modifier",
      headerName: "Modifier",
      flex: 1,
    },
    {
      field: "justification",
      headerName: "Justification",
      flex: 3,
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
            py: 1,
          }}
        >
          {params.value}
        </Box>
      ),
    },
  ];

  const getPlainText = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.innerText.trim();
  };

  return (
    <Box bgcolor={"white"} p={4} minHeight={"100vh"}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        CPT Optimizer Tool
      </Typography>

      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight={500} mb={1}>
          Enter Clinical Note
        </Typography>

        <ReactQuill
          value={note}
          onChange={setNote}
          theme="snow"
          style={{ fontSize: "14px" }}
        />
      </Box>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          onClick={fetchCPTData}
          disabled={loading || !getPlainText(note)}
        >
          {loading ? "Loading..." : "Optimize CPT Codes"}
        </Button>
      </Box>

      {!loading && errorMessage && (
        <Box mt={2}>
          <Typography color="error" fontWeight={500}>
            {errorMessage}
          </Typography>
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && rows.length > 0 && (
        <Paper elevation={3} sx={{ mt: 4, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            CPT Code Recommendations
          </Typography>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            pageSize={5}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            sx={{
              mt: 2,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: "bold",
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default CPTOptimizerTool;
